import { clsx } from 'clsx'
import * as React from 'react'

import { useSplitEnabled } from '@buffer-mono/features'
import {
  Button,
  Flex,
  Label,
  SparklesIcon,
  Text,
  Textarea,
  toast,
  VisuallyHidden,
} from '@buffer-mono/popcorn'
import { BufferTrackerReact as BufferTracker } from '@buffer-mono/tracking-plan'

import { useOrganizationId } from '~publish/contexts/AccountContext'
import { useGenerateAltText } from '~publish/legacy/ai/hooks/useGenerateAltText'
import CharacterCount from '~publish/legacy/composer/composer/components/CharacterCount'

import styles from './ComposerImageAltText.module.css'

import type { JSX } from 'react'

type ComposerImageAltTextProps = {
  /** Public image URL used for AI generation. */
  imageUrl: string
  value: string
  maxLength: number
  disabled?: boolean
  generationDisabledReason?: string
  compact?: boolean
  className?: string
  onValueChange: (value: string) => void
  onRequestSignup?: (trigger: HTMLButtonElement) => void
}

const GENERIC_GENERATION_ERROR =
  'Whoops! We could not generate a description right now. Please try again.'
const ALT_TEXT_HEADING = 'Add a description'
const ALT_TEXT_DESCRIPTION =
  'Alt text helps people with visual impairments understand the image. The text will also appear in place of the image if it fails to load.'
type AltTextGenerationStatus = 'idle' | 'pending' | 'complete'

const ComposerImageAltText = React.forwardRef<
  HTMLTextAreaElement,
  ComposerImageAltTextProps
>(function ComposerImageAltText(
  {
    imageUrl,
    value,
    maxLength,
    disabled = false,
    generationDisabledReason,
    compact = false,
    className,
    onValueChange,
    onRequestSignup,
  },
  forwardedRef,
): JSX.Element {
  const [isTextareaFocused, setIsTextareaFocused] = React.useState(false)
  const [generationStatus, setGenerationStatus] =
    React.useState<AltTextGenerationStatus>('idle')
  const generationRequestRef = React.useRef(0)
  const organizationId = useOrganizationId()
  const { isEnabled: isAltTextGeneratorEnabled, isReady: isSplitReady } =
    useSplitEnabled('ai-alt-text-generator')
  const { generateAltText, isGenerating } = useGenerateAltText(organizationId)
  const textareaId = React.useId()
  const descriptionId = `${textareaId}-description`
  const characterCountId = `${textareaId}-character-count`
  const generationDisabledReasonId = `${textareaId}-generation-disabled-reason`
  const normalizedValue = value.slice(0, maxLength)
  const remainingCharacterCount = maxLength - normalizedValue.length
  const remainingCharacterCountLabel = `${remainingCharacterCount} ${
    remainingCharacterCount === 1 ? 'character' : 'characters'
  } left`
  const isCompactCharacterCountVisible =
    compact && (isTextareaFocused || remainingCharacterCount <= maxLength * 0.1)
  const isGenerationDisabled = disabled || Boolean(generationDisabledReason)

  React.useLayoutEffect(() => {
    generationRequestRef.current += 1
    setGenerationStatus('idle')

    return (): void => {
      generationRequestRef.current += 1
    }
  }, [imageUrl, isGenerationDisabled, maxLength])

  React.useEffect(() => {
    if (value === normalizedValue) return

    onValueChange(normalizedValue)
  }, [normalizedValue, onValueChange, value])

  const handleGenerate = async (
    event: React.MouseEvent<HTMLButtonElement>,
  ): Promise<void> => {
    if (isGenerationDisabled) return

    if (onRequestSignup) {
      onRequestSignup(event.currentTarget)
      return
    }

    const requestId = generationRequestRef.current + 1
    generationRequestRef.current = requestId
    setGenerationStatus('pending')

    BufferTracker.cTAClicked({
      clientName: 'publishWeb',
      organizationId: organizationId ?? undefined,
      product: 'publish',
      cta: 'publish-composer-altTextDialog-generateAltText-1',
      upgradePathName: null,
    })

    try {
      const generatedAltText = await generateAltText(imageUrl)
      if (generationRequestRef.current !== requestId) return

      onValueChange(generatedAltText.slice(0, maxLength))
      setGenerationStatus('complete')
    } catch (error) {
      if (generationRequestRef.current !== requestId) return

      setGenerationStatus('idle')
      toast.error(
        error instanceof Error ? error.message : GENERIC_GENERATION_ERROR,
      )
    }
  }

  const textarea = (
    <Textarea
      ref={forwardedRef}
      id={textareaId}
      aria-describedby={
        isCompactCharacterCountVisible
          ? `${descriptionId} ${characterCountId}`
          : descriptionId
      }
      placeholder="Add a description for people with visual impairments"
      className={styles.textarea}
      value={normalizedValue}
      maxLength={maxLength}
      size={compact ? 'large' : undefined}
      resize={compact ? 'none' : 'auto'}
      rows={compact ? 3 : 2}
      disabled={disabled}
      onChange={(event): void => onValueChange(event.target.value)}
      onFocus={(): void => setIsTextareaFocused(true)}
      onBlur={(): void => setIsTextareaFocused(false)}
    />
  )
  const showAltTextGenerator =
    Boolean(onRequestSignup) ||
    (Boolean(organizationId) && isSplitReady && isAltTextGeneratorEnabled)

  return (
    <Flex
      direction="column"
      align="stretch"
      gap="sm"
      className={clsx(styles.container, compact && styles.compact, className)}
    >
      {compact ? (
        <div className={styles.compactHeader}>
          <Label size="medium" className={styles.label} htmlFor={textareaId}>
            {ALT_TEXT_HEADING}
          </Label>
          <Text id={descriptionId} color="subtle" size="sm">
            {ALT_TEXT_DESCRIPTION}
          </Text>
        </div>
      ) : (
        <>
          <Label size="large" className={styles.label} htmlFor={textareaId}>
            {ALT_TEXT_HEADING}
          </Label>
          <Text id={descriptionId} color="subtle">
            {ALT_TEXT_DESCRIPTION}
          </Text>
        </>
      )}
      {compact ? (
        <div className={styles.field}>
          {textarea}
          {isCompactCharacterCountVisible ? (
            <>
              <CharacterCount
                className={styles.compactCharacterCount}
                count={normalizedValue.length}
                maxCount={maxLength}
              />
              <VisuallyHidden id={characterCountId}>
                {remainingCharacterCountLabel}
              </VisuallyHidden>
            </>
          ) : null}
        </div>
      ) : (
        <Flex align="center" gap="xs" className={styles.field}>
          {textarea}
          <CharacterCount
            className={styles.characterCount}
            count={normalizedValue.length}
            maxCount={maxLength}
          />
        </Flex>
      )}
      {showAltTextGenerator ? (
        <Button
          type="button"
          variant="secondary"
          onClick={handleGenerate}
          loading={isGenerating}
          disabled={isGenerationDisabled}
          aria-busy={
            isGenerating || generationStatus === 'pending' ? true : undefined
          }
          aria-describedby={
            generationDisabledReason ? generationDisabledReasonId : undefined
          }
          className={styles.generateButton}
        >
          <SparklesIcon />
          Generate with AI
        </Button>
      ) : null}
      {showAltTextGenerator && generationDisabledReason ? (
        <Text id={generationDisabledReasonId} color="subtle" size="sm">
          {generationDisabledReason}
        </Text>
      ) : null}
      <VisuallyHidden as="output" aria-live="polite" aria-atomic="true">
        {generationStatus === 'pending'
          ? 'Generating image description.'
          : generationStatus === 'complete'
            ? 'Image description generated.'
            : ''}
      </VisuallyHidden>
    </Flex>
  )
})

export { ComposerImageAltText }
